import { Trophy, CheckCircle, AlertTriangle, X, Star } from 'lucide-react'
import React, { useContext, useState } from 'react'
import { toast } from 'react-toastify'
import { BASE_URL_API } from '../../References/urls'
import AuthContext from '../../store/auth-context'
import styles from './Mark15DayModal.module.css'

const Mark15DayModal = ({ isOpen, onClose, referralDetails, applicationData, onSuccess }) => {
    const authCtx = useContext(AuthContext)
    const [confirmedBy, setConfirmedBy] = useState('salon_confirmed')
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [inlineError, setInlineError] = useState('')
    const [successData, setSuccessData] = useState(null)

    if (!isOpen) return null

    const joiningDate = applicationData?.joiningDate?.split(' ')[0]
    const dayCount = joiningDate && joiningDate !== '0000-00-00'
        ? Math.floor((new Date() - new Date(joiningDate)) / 86400000)
        : null

    const handleConfirm = async () => {
        setInlineError('')
        setIsSubmitting(true)

        try {
            const response = await fetch(BASE_URL_API + "/referralMark15DayComplete", {
                method: "POST",
                body: JSON.stringify({
                    token: authCtx.token,
                    opsUserId: authCtx.userIdentifier,
                    referralId: referralDetails.referralId,
                    confirmedBy
                })
            })

            if (!response.ok) {
                setInlineError("Save nahi hua. Retry karo.")
                setIsSubmitting(false)
                return
            }

            const respo = await response.json()

            if (respo.status === true) {
                setSuccessData(respo.response)
                onSuccess()
            } else {
                const errorType = respo.response?.errorType
                if (errorType === "NOT_IN_HIRED_STATE") {
                    toast.warn("Candidate abhi hired state mein nahi hai. Pehle hiring confirm karo.")
                    onClose()
                } else if (errorType === "ALREADY_COMPLETED") {
                    toast("15-day pehle se confirm ho chuka hai.")
                    onSuccess()
                    onClose()
                } else if (errorType === "MISSING_FIELDS") {
                    setInlineError("Kuch fields missing hain. Dobara try karo.")
                } else if (errorType === "INVALID_CONFIRMER") {
                    setInlineError("Invalid confirmation source. Ek option select karo.")
                } else if (errorType === "REWARD_NOT_FOUND") {
                    setInlineError("Reward record nahi mila. Support ko batao.")
                } else if (errorType === "WALLET_NOT_FOUND") {
                    setInlineError("Referrer ka wallet nahi mila. Support ko batao.")
                } else {
                    setInlineError("Mark 15-day failed. Please retry.")
                }
            }
        } catch {
            setInlineError("Mark 15-day failed. Please retry.")
        }

        setIsSubmitting(false)
    }

    const referrerCode = referralDetails?.referred_by_code

    return (
        <div className={styles.overlay}>
            <div className={styles.modal}>
                {successData ? (
                    <div className={styles.successState}>
                        <div className={`${styles.successBanner} ${successData.tierLevelUp ? styles.successBannerCelebration : ''}`}>
                            <div className={styles.successTitle}>
                                {successData.tierLevelUp ? <Star size={18} /> : <CheckCircle size={18} />}
                                {successData.tierLevelUp ? '15-day complete!' : '15-day complete!'}
                            </div>
                            <div>₹{successData.rewardCredited?.toLocaleString()} → {referrerCode} ke wallet mein add ho gaya</div>
                            {successData.tierLevelUp && (
                                <div className={styles.tierUpBadge}>
                                    <Star size={14} />
                                    TIER UP! {referrerCode} ab "{successData.newTierLabel}" ban gaya!
                                    <span className={styles.placementCount}>{successData.placementsThisMonth} placements this month</span>
                                </div>
                            )}
                            {!successData.tierLevelUp && (
                                <div className={styles.tierInfo}>
                                    Tier: {successData.newTierLabel} ({successData.placementsThisMonth} placement this month)
                                </div>
                            )}
                        </div>
                        <button className={styles.closeBtn} onClick={onClose}>Close</button>
                    </div>
                ) : (
                    <>
                        <div className={styles.modalHeader}>
                            <Trophy size={18} />
                            Confirm 15-Day Completion
                        </div>

                        <div className={styles.infoRow}>
                            <span className={styles.infoLabel}>Candidate:</span>
                            <strong>{applicationData?.stylist_name}</strong>
                        </div>
                        <div className={styles.infoRow}>
                            <span className={styles.infoLabel}>Salon:</span>
                            <strong>{applicationData?.company_name}</strong>
                        </div>
                        {joiningDate && joiningDate !== '0000-00-00' && (
                            <div className={styles.infoRow}>
                                <span className={styles.infoLabel}>Joined:</span>
                                <strong>{new Date(joiningDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</strong>
                                <span className={styles.dayChip}>Day {dayCount} today</span>
                            </div>
                        )}

                        {dayCount !== null && dayCount < 15 && (
                            <div className={styles.warningBanner}>
                                <AlertTriangle size={14} />
                                15 din abhi complete nahi hue ({dayCount}/15). Sure ho?
                            </div>
                        )}

                        <div className={styles.fieldWrapper}>
                            <div className={styles.fieldLabel}>Confirmed by:</div>
                            <label className={styles.radioOption}>
                                <input
                                    type="radio"
                                    name="confirmedBy"
                                    value="ops_verified"
                                    checked={confirmedBy === 'ops_verified'}
                                    onChange={() => setConfirmedBy('ops_verified')}
                                />
                                Ops Team verification
                            </label>
                            <label className={styles.radioOption}>
                                <input
                                    type="radio"
                                    name="confirmedBy"
                                    value="salon_confirmed"
                                    checked={confirmedBy === 'salon_confirmed'}
                                    onChange={() => setConfirmedBy('salon_confirmed')}
                                />
                                Salon confirmed <span className={styles.recommendedTag}>(recommended)</span>
                            </label>
                        </div>

                        <div className={styles.rewardNote}>
                            <div className={styles.rewardNoteTitle}>Yeh action karne se:</div>
                            <div>• ₹1,000 referrer ke wallet mein add hoga</div>
                            <div>• Referrer ki tier update hogi</div>
                        </div>

                        {inlineError && (
                            <div className={styles.inlineError}>
                                <AlertTriangle size={14} />
                                {inlineError}
                            </div>
                        )}

                        <div className={styles.actions}>
                            <button className={styles.cancelBtn} onClick={onClose} disabled={isSubmitting}>
                                <X size={14} />
                                Cancel
                            </button>
                            <button
                                className={styles.confirmBtn}
                                onClick={handleConfirm}
                                disabled={isSubmitting}
                            >
                                <Trophy size={14} />
                                {isSubmitting ? 'Processing...' : 'Confirm 15 Days'}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    )
}

export default Mark15DayModal
